import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config';
import {
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Grid,
    IconButton,
    Alert,
    Snackbar,
    CircularProgress,
    ImageList,
    ImageListItem
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import CreatePinModal from './CreatePinModal';

const PLACEHOLDER_IMG = 'https://via.placeholder.com/300x180?text=No+Image';

const ProjectBoard = () => {
    const [boards, setBoards] = useState([]);
    const [selectedBoard, setSelectedBoard] = useState(null);
    const [openCreatePin, setOpenCreatePin] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();

    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/login');
            return;
        }
        fetchBoards();
    }, [isAuthenticated, navigate]);

    const fetchBoards = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            const response = await axios.get(`${API_URL}/api/boards`, {
                headers: { 'x-auth-token': token }
            });
            // Ensure each board has an items array
            const boardsWithItems = response.data.map(board => ({
                ...board,
                items: board.items || []
            }));
            setBoards(boardsWithItems);
            setError(null);
        } catch (err) {
            console.error('Error fetching boards:', err);
            setError(err.response?.data?.message || 'Failed to fetch boards');
        } finally {
            setLoading(false);
        }
    };

    const handleCreatePin = async (pinData) => {
        if (pinData.board === '__new__') {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');
                const response = await axios.post(
                    `${API_URL}/api/boards`,
                    { name: pinData.title, description: pinData.description, items: [pinData] },
                    { headers: { 'x-auth-token': token } }
                );
                setBoards([...boards, { ...response.data, items: [pinData] }]);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to create board');
            } finally {
                setLoading(false);
            }
        } else {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                if (!token) throw new Error('No authentication token found');
                const boardToUpdate = boards.find(b => b._id === pinData.board || b.name === pinData.board);
                if (!boardToUpdate) throw new Error('Board not found');
                const updatedItems = [...(boardToUpdate.items || []), pinData];
                const response = await axios.put(
                    `${API_URL}/api/boards/${boardToUpdate._id}`,
                    { ...boardToUpdate, items: updatedItems },
                    { headers: { 'x-auth-token': token } }
                );
                setBoards(boards.map(b => b._id === boardToUpdate._id ? { ...response.data } : b));
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to add pin to board');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleDeleteBoard = async (boardId) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            await axios.delete(`${API_URL}/api/boards/${boardId}`, {
                headers: { 'x-auth-token': token }
            });
            setBoards(boards.filter(board => board._id !== boardId));
            setError(null);
        } catch (err) {
            console.error('Error deleting board:', err);
            setError(err.response?.data?.message || 'Failed to delete board');
        } finally {
            setLoading(false);
        }
    };

    const handleBoardClick = (board) => {
        setSelectedBoard(board);
    };

    const handleImageDrop = async (e, boardId) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file || !file.type.startsWith('image/')) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) throw new Error('No authentication token found');
            // Create a new board item for the image
            const formData = new FormData();
            formData.append('type', 'image');
            formData.append('content', file);
            // We'll use a dedicated endpoint for image upload, or just add to items
            // For now, add as a board item
            const reader = new FileReader();
            reader.onload = async (event) => {
                const imagePreview = event.target.result;
                // Add the image as a board item
                const board = boards.find(b => b._id === boardId);
                if (!board) return;
                const newItem = { type: 'image', image: imagePreview, imagePreview };
                const updatedItems = [...(board.items || []), newItem];
                const response = await axios.put(
                    `${API_URL}/api/boards/${boardId}`,
                    { ...board, items: updatedItems },
                    { headers: { 'x-auth-token': token } }
                );
                setBoards(boards.map(b => b._id === boardId ? { ...response.data } : b));
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setError('Failed to add image to board');
        } finally {
            setLoading(false);
        }
    };

    if (!isAuthenticated) {
        return null;
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Project Boards</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenCreatePin(true)}
                    disabled={loading}
                >
                    Create New Board
                </Button>
            </Box>

            {error && (
                <Snackbar
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={() => setError(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert onClose={() => setError(null)} severity="error">
                        {error}
                    </Alert>
                </Snackbar>
            )}

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            <Grid container spacing={3}>
                {boards.map((board) => (
                    <Grid item xs={12} sm={6} md={4} key={board._id}>
                        <Card
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => handleImageDrop(e, board._id)}
                            sx={{ position: 'relative', border: '2px dashed #ccc', transition: 'border 0.2s', '&:hover': { border: '2px solid #1976d2' } }}
                        >
                            <Box sx={{ height: 180, background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
                                    {(board.items || []).filter(item => item.type === 'image').slice(0, 3).map((item, idx) => {
                                        let imgSrc = item.imagePreview || item.image;
                                        return (
                                            <img
                                                key={idx}
                                                src={imgSrc || PLACEHOLDER_IMG}
                                                alt={board.name}
                                                style={{ maxHeight: '100%', maxWidth: '33%', objectFit: 'cover', borderRadius: 8 }}
                                            />
                                        );
                                    })}
                                </Box>
                            </Box>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6">{board.name}</Typography>
                                    <IconButton
                                        onClick={() => handleDeleteBoard(board._id)}
                                        color="error"
                                        disabled={loading}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                                <Typography color="textSecondary" gutterBottom>
                                    {board.description}
                                </Typography>
                                <Typography variant="body2">
                                    {(board.items || []).length} items
                                </Typography>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    sx={{ mt: 2 }}
                                    onClick={() => handleBoardClick(board)}
                                    disabled={loading}
                                >
                                    Open Board
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <CreatePinModal
                open={openCreatePin}
                onClose={() => setOpenCreatePin(false)}
                boards={[...boards, { _id: '__new__', name: 'Create new board...' }]}
                onCreate={handleCreatePin}
            />

            {selectedBoard && (
                <Dialog
                    open={!!selectedBoard}
                    onClose={() => setSelectedBoard(null)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>{selectedBoard.name}</DialogTitle>
                    <DialogContent>
                        <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                            {selectedBoard.description}
                        </Typography>
                        {selectedBoard.items && selectedBoard.items.length > 0 ? (
                            <ImageList cols={3} gap={16} sx={{ width: '100%', maxHeight: 500 }}>
                                {selectedBoard.items.map((item, idx) => {
                                    let imgSrc = item.imagePreview;
                                    if (!imgSrc && item.image) {
                                        if (typeof item.image === 'string') {
                                            imgSrc = item.image;
                                        } else if (item.image instanceof File) {
                                            imgSrc = URL.createObjectURL(item.image);
                                        }
                                    }
                                    if (!imgSrc) return null;
                                    return (
                                        <ImageListItem key={idx}>
                                            <img
                                                src={imgSrc}
                                                alt={item.title || `Pin ${idx + 1}`}
                                                style={{ width: '100%', height: 'auto', borderRadius: 8 }}
                                            />
                                            <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                                                {item.title}
                                            </Typography>
                                        </ImageListItem>
                                    );
                                })}
                            </ImageList>
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                No images in this board yet.
                            </Typography>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setSelectedBoard(null)}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default ProjectBoard; 